// Wind session detail page — stats / range-bar / levels / cycles table.
// Composes useWindDetail + useLivePrice for real-time updates.
import { useParams } from "react-router-dom";
import { useWindDetail } from "@/hooks/use-wind-detail";
import { useLivePrice } from "@/providers/ws-provider";
import WindStats from "@/components/wind/wind-stats";
import WindRangeBar from "@/components/wind/wind-range-bar";
import WindLevelsView from "@/components/wind/wind-levels-view";
import WindCyclesTable from "@/components/wind/wind-cycles-table";
import WindActions from "@/components/wind/wind-actions";
import AiAdviceCard from "@/components/ai/ai-advice-card";
import { useDetailActions } from "@/components/shell/detail-layout";

export default function WindDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, error } = useWindDetail(id);

  // Subscribe to live price for the session's symbol
  const symbol = data?.session?.symbol ?? "";
  const livePrice = useLivePrice(symbol);

  // Mount action buttons into DetailLayout's sticky sub-header slot.
  useDetailActions(data ? <WindActions data={data} /> : null);

  if (isLoading) {
    return (
      <div className="text-muted text-sm text-center py-12">Loading…</div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-danger text-sm text-center py-12">
        Failed to load wind detail
      </div>
    );
  }

  return (
    <div>
      {/* 6-stat header */}
      <WindStats data={data} />

      {/* Range bar — hidden when no active cycle */}
      <WindRangeBar
        session={data.session}
        latestCycle={data.latest_cycle}
        livePrice={livePrice}
      />

      {/* Levels view — TP/entry/current/SL or chasing-maker state */}
      <WindLevelsView
        session={data.session}
        latestCycle={data.latest_cycle}
        livePrice={livePrice}
      />

      {/* AI Advisor */}
      <AiAdviceCard
        entityType="wind"
        entityId={data.session.id}
        aiEnabled={Boolean(data.session.ai_enabled)}
        lastAdvice={data.latest_cycle?.ai_advice}
      />

      {/* Cycles table */}
      <section>
        <div className="text-xs text-muted uppercase tracking-wider mb-2 font-medium">
          Cycles
        </div>
        <WindCyclesTable cycles={data.cycles} session={data.session} />
      </section>
    </div>
  );
}
